import { sleep } from "@/lib/utils";

import type { Api, CarrierAdmin, CarrierId } from "../types";

// Офлайн-зеркало /admin/delivery/carriers: на стенде без шлюза ключей нет ни у
// кого, как и на проде у ПЭК и Деловых линий до договоров.
const note: Record<CarrierId, string> = {
	sdek: "Города — вся сеть СДЭК: калькулятор считает тариф в любой город, где у СДЭК есть пункты или курьер. Посылочные тарифы — до небольших габаритов, крупное — экспресс и сборный груз по цене СДЭК.",
	pecom: "Города — сеть терминалов ПЭК (справочник branches/all). Крупногабарит: расчёт по весу, объёму и самой длинной стороне.",
	dellin: "Города — сеть терминалов Деловых линий (справочник request_terminals). Крупногабарит: расчёт по весу, объёму и габаритам места.",
};

const carriers: CarrierAdmin[] = [
	{
		id: "sdek",
		title: "СДЭК",
		enabled: true,
		configured: true,
		active: true,
		status: "active",
		missing: [],
		baseUrl: "https://api.cdek.ru/v2",
		capabilities: { courier: true, pickupPoints: true, terminals: false, heavyFreight: false, labels: true, webhooks: true },
		coverage: { note: note.sdek, courierZones: [] },
	},
	{
		id: "pecom",
		title: "ПЭК",
		enabled: false,
		configured: false,
		active: false,
		status: "no_keys",
		missing: ["PECOM_LOGIN", "PECOM_API_KEY"],
		baseUrl: "https://kabinet.pecom.ru/api/v1",
		capabilities: { courier: true, pickupPoints: false, terminals: true, heavyFreight: true, labels: true, webhooks: false },
		coverage: { note: note.pecom, courierZones: [] },
	},
	{
		id: "dellin",
		title: "Деловые линии",
		enabled: false,
		configured: false,
		active: false,
		status: "no_keys",
		missing: ["DELLIN_APP_KEY", "DELLIN_LOGIN", "DELLIN_PASSWORD"],
		baseUrl: "https://api.dellin.ru",
		capabilities: { courier: true, pickupPoints: false, terminals: true, heavyFreight: true, labels: true, webhooks: false },
		coverage: { note: note.dellin, courierZones: [] },
	},
];

const find = (id: CarrierId): CarrierAdmin => {
	const c = carriers.find((x) => x.id === id);
	if (!c) throw new Error("Перевозчик не найден");
	return c;
};
const recompute = (c: CarrierAdmin) => {
	c.active = c.enabled && c.configured;
	c.status = c.active ? "active" : c.configured ? "disabled" : "no_keys";
};
const copy = (c: CarrierAdmin): CarrierAdmin => structuredClone(c);

export const deliveryCarriers: Api["deliveryCarriers"] = {
	async list() {
		await sleep(120);
		return carriers.map(copy);
	},
	async setEnabled(id, enabled) {
		await sleep(150);
		const c = find(id);
		c.enabled = enabled;
		recompute(c);
		return copy(c);
	},
	async upsertZone(id, city, enabled) {
		await sleep(120);
		const c = find(id);
		const z = c.coverage.courierZones.find((x) => x.city.toLowerCase() === city.trim().toLowerCase());
		if (z) z.enabled = enabled;
		else c.coverage.courierZones.push({ id: `dz_${Date.now()}`, city: city.trim(), enabled });
		return copy(c);
	},
	async removeZone(id, zoneId) {
		await sleep(120);
		const c = find(id);
		c.coverage.courierZones = c.coverage.courierZones.filter((z) => z.id !== zoneId);
		return copy(c);
	},
	async check(id, input) {
		await sleep(300);
		const c = find(id);
		if (!c.configured) return { carrier: id, city: input.city, ok: false, reason: "no_keys", rates: [], points: 0 };
		return {
			carrier: id,
			city: input.city,
			ok: true,
			rates: [
				{ carrier: id, serviceCode: "136", name: "Посылка склад-склад", destination: "pickup_point", price: 235, daysMin: 2, daysMax: 3 },
				{ carrier: id, serviceCode: "137", name: "Посылка склад-дверь", destination: "door", price: 380, daysMin: 2, daysMax: 3 },
			],
			points: 9,
		};
	},
};
